import { useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import SeoHead from '../components/SeoHead';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  selectIsAuthenticated,
  selectProfile,
  selectProfileFetchStatus,
  selectProfileUpsertStatus,
} from '../store/selectors/profile-selectors';
import { fetchProfile, upsertProfileThunk } from '../store/thunks/profile-thunks';

/**
 * Newsletter signup surface (round-22 Phase D — realigned to
 * the shipped Smithy contract).
 *
 * Public-facing placeholder opt-in surface. Unauthenticated
 * visitors see a "Sign in to subscribe" CTA that routes to
 * `/sign-in?next=%2Fnewsletter`. Authenticated visitors see the
 * newsletter status derived from the profile row's
 * `optInNewsletter` field; a "Sign me up" button toggles the
 * flag via `UpsertProfileV1`.
 *
 * The upsert payload includes the required identity fields
 * (username, firstName, lastName) pulled from the current
 * profile so the backend's re-validation passes — the SPA never
 * calls `UpsertProfileV1` with only the newsletter flag.
 */

const PAGE_TITLE = 'DVGUP Newsletter';
const PAGE_BODY =
  'The DVGUP newsletter is launching soon. Sign up now to be notified when it goes live.';
const CONFIRMATION_MESSAGE =
  "You're signed up. We'll let you know when the newsletter goes live.";

function NewsletterIntro(): JSX.Element {
  return (
    <>
      <Typography variant="h1" component="h1" sx={{ fontSize: '1.75rem', mb: 2 }}>
        {PAGE_TITLE}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        {PAGE_BODY}
      </Typography>
    </>
  );
}

function SignInCta(): JSX.Element {
  return (
    <Button
      variant="contained"
      href={`/sign-in?next=${encodeURIComponent('/newsletter')}`}
    >
      Sign in to subscribe
    </Button>
  );
}

function NewsletterLoading(): JSX.Element {
  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
      <CircularProgress size={24} />
      <Typography>Loading…</Typography>
    </Stack>
  );
}

function NewsletterConfirmation(): JSX.Element {
  return (
    <Stack spacing={1}>
      <Typography variant="body1">{CONFIRMATION_MESSAGE}</Typography>
      <MuiLink href="/profile">manage preferences</MuiLink>
    </Stack>
  );
}

interface SignUpButtonProps {
  readonly onClick: () => void;
  readonly disabled: boolean;
  readonly error?: string;
}

function SignUpButton(props: SignUpButtonProps): JSX.Element {
  return (
    <Stack spacing={2}>
      {props.error !== undefined && (
        <Alert severity="error" role="alert">
          {props.error}
        </Alert>
      )}
      <Button variant="contained" onClick={props.onClick} disabled={props.disabled}>
        Sign me up
      </Button>
    </Stack>
  );
}

export default function NewsletterPage(): JSX.Element {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const profile = useAppSelector(selectProfile);
  const fetchStatus = useAppSelector(selectProfileFetchStatus);
  const upsertStatus = useAppSelector(selectProfileUpsertStatus);
  const upsertError = useAppSelector((state) => state.profile.upsertError);

  useEffect(() => {
    if (isAuthenticated) {
      void dispatch(fetchProfile());
    }
  }, [dispatch, isAuthenticated]);

  function handleSignMeUp(): void {
    if (profile === undefined) return;
    void dispatch(
      upsertProfileThunk({
        username: profile.username,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneE164: profile.phoneE164,
        preferredPlatforms: profile.preferredPlatforms,
        optInNewsletter: true,
        optInPaidFeatures: profile.optInPaidFeatures,
      }),
    );
  }

  return (
    <Card sx={{ maxWidth: 480, mx: 'auto', mt: 4 }}>
      <CardContent>
        <SeoHead
          route="/newsletter"
          pageTitle="Newsletter"
          branding={brandingFixture.share}
          jsonLdType="WebSite"
        />
        <NewsletterIntro />
        {renderBranch({
          isAuthenticated,
          profile,
          fetchStatus,
          upsertStatus,
          upsertError,
          onSignMeUp: handleSignMeUp,
        })}
      </CardContent>
    </Card>
  );
}

interface BranchArgs {
  readonly isAuthenticated: boolean;
  readonly profile: ReturnType<typeof selectProfile>;
  readonly fetchStatus: ReturnType<typeof selectProfileFetchStatus>;
  readonly upsertStatus: ReturnType<typeof selectProfileUpsertStatus>;
  readonly upsertError: string | undefined;
  readonly onSignMeUp: () => void;
}

function renderBranch(args: BranchArgs): JSX.Element {
  if (!args.isAuthenticated) return <SignInCta />;
  if (args.profile === undefined) return <NewsletterLoading />;
  if (args.profile.optInNewsletter === true) return <NewsletterConfirmation />;
  return (
    <SignUpButton
      onClick={args.onSignMeUp}
      disabled={args.upsertStatus === 'loading'}
      error={args.upsertStatus === 'failed' ? args.upsertError : undefined}
    />
  );
}
